import { useState } from 'react';
import { useDistributeLeads, useTeams, useUpdateTeam } from '../api/hooks';
import type { DistributeResult, Team } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { TeamRoster } from '../components/TeamRoster';
import { ErrorBanner } from '../components/ui';

/**
 * A manager's own team: who is on it, and how new leads reach them.
 *
 * The two belong together. Auto-distribute is meaningless without a roster to
 * distribute to, and the commonest confusion — "it's on but nothing moves" —
 * is answered by the empty member list sitting directly above the switch.
 */
export function TeamDistributionPage() {
  const { user } = useAuth();
  const { data: teams = [], isPending, error } = useTeams();
  const updateTeam = useUpdateTeam();
  const distribute = useDistributeLeads();
  const [lastResult, setLastResult] = useState<DistributeResult | null>(null);

  const myTeams = teams.filter((t) => t.manager?.id === user?.id);
  // An admin with no team of their own can still see and configure every
  // team's routing from here, rather than needing a manager's login.
  const shown = myTeams.length > 0 ? myTeams : user?.scope === 'all' ? teams : [];

  return (
    <div className="page" style={{ maxWidth: 760 }}>
      <div className="page-head">
        <div>
          <h1>My team</h1>
          <p className="sub">Who is on your team, and how leads reach them.</p>
        </div>
      </div>

      <ErrorBanner error={error ?? updateTeam.error ?? distribute.error} />

      {lastResult && (
        <div className="banner banner-info">
          <div>
            Distributed {lastResult.assigned} lead{lastResult.assigned === 1 ? '' : 's'} across{' '}
            {lastResult.team_name}: {lastResult.by_member.map((m) => `${m.full_name} (${m.count})`).join(', ')}
            {lastResult.skipped.length > 0 && ` — ${lastResult.skipped.length} skipped`}
          </div>
        </div>
      )}

      {isPending ? (
        <div className="skeleton" style={{ height: 140, borderRadius: 14 }} />
      ) : shown.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <p style={{ margin: 0, color: 'var(--muted)' }}>
              You don't currently manage a team, so there's nothing to configure here. Ask an admin to
              set you as a team's manager first — from Administration → Teams.
            </p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {shown.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              busy={updateTeam.isPending}
              distributing={distribute.isPending}
              onToggle={(enabled) => updateTeam.mutate({ id: team.id, auto_distribute: enabled })}
              onTimeChange={(time) => updateTeam.mutate({ id: team.id, auto_distribute_time: time })}
              onDistributeNow={() =>
                distribute.mutate(
                  { team_ids: [team.id] },
                  { onSuccess: (result) => setLastResult(result) },
                )
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TeamCard({
  team,
  busy,
  distributing,
  onToggle,
  onTimeChange,
  onDistributeNow,
}: {
  team: Team;
  busy: boolean;
  distributing: boolean;
  onToggle: (enabled: boolean) => void;
  onTimeChange: (time: string | null) => void;
  onDistributeNow: () => void;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <h2>{team.name}</h2>
        <div className="card-actions">
          <span className="chip chip-plain">{team.member_count} members</span>
        </div>
      </div>
      <div className="card-body">
        <TeamRoster team={team} />

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 16,
            borderTop: '1px solid var(--rule)',
            marginTop: 18,
            paddingTop: 16,
          }}
        >
          <label className="switch" style={{ marginTop: 2 }}>
            <input
              type="checkbox"
              checked={team.auto_distribute}
              disabled={busy}
              onChange={(e) => onToggle(e.target.checked)}
            />
            <span className="track" />
          </label>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 650, fontSize: 14.5 }}>Auto-distribute new leads</div>
            <p className="hint" style={{ marginTop: 4, maxWidth: '52ch' }}>
              {team.auto_distribute
                ? 'On — leads routed to you skip straight to a team member instead of sitting with you.'
                : 'Off — leads routed to you sit with you until you assign them or distribute manually.'}
            </p>

            {team.auto_distribute && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <label htmlFor={`time-${team.id}`} style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>
                  Distribute
                </label>
                <select
                  id={`time-mode-${team.id}`}
                  style={{ width: 'auto' }}
                  value={team.auto_distribute_time ? 'scheduled' : 'instant'}
                  disabled={busy}
                  onChange={(e) => onTimeChange(e.target.value === 'instant' ? null : '09:00')}
                >
                  <option value="instant">Instantly, as each lead arrives</option>
                  <option value="scheduled">Once a day, at a set time</option>
                </select>
                {team.auto_distribute_time && (
                  <input
                    id={`time-${team.id}`}
                    type="time"
                    style={{ width: 150, minWidth: 150, flexShrink: 0 }}
                    value={team.auto_distribute_time}
                    disabled={busy}
                    onChange={(e) => onTimeChange(e.target.value || null)}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--rule)', marginTop: 16, paddingTop: 14 }}>
          <button className="btn" disabled={distributing} onClick={onDistributeNow}>
            {distributing ? <span className="spinner" /> : 'Distribute unassigned to team now'}
          </button>
          <p className="hint" style={{ marginTop: 8, marginBottom: 0 }}>
            One-off: round-robins every lead still sitting with you for {team.name} across its active
            members, right away — independent of the schedule above.
          </p>
        </div>
      </div>
    </div>
  );
}
